"use client";

import dynamic from "next/dynamic";
import { FC } from "react";
import { useTranslations } from "next-intl";

import { FilterType, FiltersLayout, SearchType, ICoordinates } from "@types";
import { SEARCH_OFFICES_QUERY } from "@graphql";
import { DEFAULT_SEARCH_RADIUS } from "consts";

import { Loader } from "components/atoms/Loader/Loader";

const Search = dynamic(
  () =>
    import("components/organisms/Search/SearchContainer").then((module) => ({
      default: module.SearchContainer,
    })),
  { loading: () => <Loader /> },
);

interface Props {
  municipality?: string;
  officeCoordinates?: ICoordinates;
  officesCount: number;
  preview?: boolean;
}

export const OfficesSearch: FC<Props> = ({
  municipality,
  officeCoordinates,
  officesCount,
  preview,
}) => {
  const t = useTranslations("search");

  return (
    <Search
      query={SEARCH_OFFICES_QUERY}
      type={SearchType.Offices}
      filters={[
        {
          key: "term",
          type: FilterType.Search,
          label: t("search-name"),
          placeholder: `${t("office-name")}...`,
        },
        {
          key: "orderBy",
          type: FilterType.Dropdown,
          label: t("sort-by"),
          options: [
            {
              value: "title_ASC",
              label: "A-Z",
            },
            {
              value: "title_DESC",
              label: "Z-A",
            },
          ],
        },
        {
          key: "municipality",
          type: FilterType.Location,
          label: t("search-municipality"),
          settings: {
            showCurrentLocation: true,
          },
          defaultValueLabel: municipality,
        },
        {
          key: "radius",
          type: FilterType.Dropdown,
          label: t("radius"),
          options: [
            {
              value: 5,
              label: "< 5km",
            },
            {
              value: 10,
              label: "< 10km",
            },
            {
              value: 15,
              label: "< 15km",
            },
            {
              value: 20,
              label: "< 20km",
            },
            {
              value: 25,
              label: "< 25km",
            },
          ],
        },
      ]}
      itemsCount={officesCount}
      defaultValues={{
        orderBy: "title_ASC",
        ...(officeCoordinates && {
          municipality: {
            label: municipality,
            coordinates: {
              latitude: officeCoordinates?.latitude,
              longitude: officeCoordinates?.longitude,
            },
          },
        }),
        radius: DEFAULT_SEARCH_RADIUS,
      }}
      layout={FiltersLayout.GridTwoThird}
      preview={preview}
    />
  );
};
